<template>
    <div class="zap-column">
        <!-- HEADER (sticky) -->
        <div class="zap-col-header">
            <div class="zap-col-athlete">
                <div class="zap-col-avatar" :style="gradientStyle">{{ initials }}</div>
                <div class="zap-col-name">{{ studentName }}</div>
                <div class="zap-mini-pct">{{ percentReady }}%</div>
            </div>
            <div class="zap-col-progress-track">
                <div class="zap-col-progress-fill" :style="{ width: percentReady + '%' }"></div>
            </div>
        </div>

        <!-- STATS -->
        <div class="zap-col-stats">
            <div class="zap-col-stat-pill">
                <span class="label">{{ $tc('common.minutes', 2) }}</span>
                <span class="val">{{ routineDuration }}</span>
            </div>
            <div class="zap-col-stat-pill">
                <span class="label">{{ $tc('common.blocks', 2) }}</span>
                <span class="val">{{ blocks.length }}</span>
            </div>
            <div class="zap-col-stat-pill">
                <span class="label">{{ $tc('exercises.exercises', 2) }}</span>
                <span class="val">{{ exercisesQuantity }}</span>
            </div>
        </div>

        <!-- BLOCKS -->
        <div class="zap-col-blocks">
            <div v-for="(block, i) in blocks" :key="i" class="zap-col-block"
                :class="{ open: openBlocks.includes(i), done: block.completed }">
                <div class="zap-col-block-header" @click="toggleBlock(i)">
                    <div class="zap-col-toggle" :class="{ loading: loadingComplete && completingIndex === i }"
                        @click.stop="onToggleBlock(block, i)"></div>
                    <div class="zap-col-block-title">
                        {{ $tc('common.blocks', 1) }} {{ i + 1 }} {{ circuitType(block) }}
                    </div>
                    <div class="zap-col-block-meta">{{ blockMeta(block) }}</div>
                    <div class="zap-col-block-chevron"><v-icon size="14">mdi-chevron-down</v-icon></div>
                </div>
                <div class="zap-col-exercises" v-show="openBlocks.includes(i)">
                    <template v-for="(exercise, j) in block.exercises">
                        <exercise-card compact :key="`ex-${j}`" :exercise="exercise" :block="block" show-complete-sets
                            no-redirect withMenu @click:image="onClickExerciseImage"
                            @click:config="onClickExerciseConfig(exercise, block, i, j)"
                            @click:menu:timer="handleTimer"
                            @click:menu:exercise="handleExercise(exercise, i, j)"
                            @click:menu:camera="handleCamera(exercise, i, j)" @click:menu:skip="handleSkip(exercise, i)"
                            @click:menu:history="handleHistory(exercise)"
                            @click:menu:pain="handlePainScale(exercise, i)" />
                        <div v-if="exercise.hasTime" :key="`rest-${j}`" class="zap-col-rest">
                            <v-icon size="13">mdi-alarm-snooze</v-icon>
                            {{ $t('exercises.rest') }} {{ exercise.time }}
                        </div>
                    </template>
                    <div v-if="block.isCircuit && !block.completed" class="zap-col-circuit-action">
                        <v-btn small depressed fab color="primary" class="black--text" :loading="loadingComplete"
                            @click="onToggleBlock(block, i)">
                            <v-icon>mdi-timer-outline</v-icon>
                        </v-btn>
                    </div>
                </div>
            </div>
        </div>

        <!-- DIALOGS -->
        <watch-video-dialog v-if="video" :thumb="img" :video="video" ref="watchVideo" />
        <edit-sets-dialog ref="editSets" @updatedSet="onUpdatedSet" @sentCommentary="onSentCommentary" />
        <change-exercise-dialog ref="changeExercise" :exercise="exerciseEditing"
            :coach-id="assignedRoutine && assignedRoutine.coachId" @success="onChangeExerciseSuccess" />
        <timer-dialog :duration="-1" infinite closable ref="timer" />
        <timer-dialog v-if="circuitBlock" :type="circuitBlock.type" :duration="circuitBlock.sets * 60"
            :tabata="circuitBlock.tabata" @hide="onCircuitTimerHide" ref="circuitTimer" />
        <pain-scale-dialog ref="painScale" @success="onPainScale" />
        <record-video-dialog ref="recordVideo" :exercise="exerciseEditing"
            :assigned-routine-id="assignedRoutine && assignedRoutine.id" />
        <exercise-history-dialog :routine="assignedRoutine" ref="history" />
    </div>
</template>

<script>

import ExerciseCard from "@/components/common/routines/ExerciseCard";
import { mapActions, mapState } from "vuex";
import { db, FieldValue } from "@/utils/Firebase";

import { blockUniqueTime } from "@/utils/Exercises";
import { getInitials, getAvatarGradient } from "@/utils/Common";
import WatchVideoDialog from '../../common/routines/WatchVideoDialog.vue';
import EditSetsDialog from '../../common/routines/EditSetsDialog.vue';
import ChangeExerciseDialog from '../../common/ChangeExerciseDialog.vue';
import TimerDialog from '../../common/routines/TimerDialog.vue';
import PainScaleDialog from '../../common/PainScaleDialog.vue';
import RecordVideoDialog from '../../common/RecordVideoDialog.vue';
import ExerciseHistoryDialog from './ExerciseHistoryDialog.vue';

export default {
    props: {
        routine: {
            type: Object,
            default: null,
        },
    },
    components: {
        ExerciseCard,
        WatchVideoDialog,
        EditSetsDialog,
        ChangeExerciseDialog,
        TimerDialog,
        PainScaleDialog,
        RecordVideoDialog,
        ExerciseHistoryDialog,
    },
    data() {
        return {
            assignedRoutine: null,
            openBlocks: [0],
            loadingComplete: false,
            completingIndex: null,
            circuitBlockIndex: null,
            video: null,
            img: null,
            exerciseEditing: null,
        }
    },
    computed: {
        ...mapState(['equipment']),
        studentName(vm = this) {
            return vm.assignedRoutine?.student?.name || vm.$t('common.not_available_short')
        },
        initials(vm = this) {
            return getInitials(vm.assignedRoutine?.student?.name)
        },
        gradientStyle(vm = this) {
            const g = getAvatarGradient(vm.assignedRoutine?.studentId || vm.assignedRoutine?.student?.name || '')
            return { background: g.background, color: g.color }
        },
        blocks(vm = this) {
            return blockUniqueTime(vm.assignedRoutine?.blocks || [])
        },
        circuitBlock(vm = this) {
            if (vm.circuitBlockIndex === null) return null
            return vm.blocks[vm.circuitBlockIndex] || null
        },
        routineDuration(vm = this) {
            if (!vm.assignedRoutine) return vm.$t('common.not_available_short')

            let minutes = 0

            vm.assignedRoutine.blocks.forEach(block => {
                block.exercises.forEach(exercise => {
                    minutes += exercise?.config?.sets?.length || block.sets || 0
                })
            })

            let minutesAdded = Number(minutes * 1.3).toFixed()

            if (Number(minutes) === Number(minutesAdded)) return minutes
            return `${minutes}-${minutesAdded}`
        },
        percentReady(vm = this) {
            if (!vm.assignedRoutine) return 0
            let qtyExercises = 0
            let completed = 0

            vm.assignedRoutine.blocks.forEach(block => {
                let exercises = block.exercises.filter(ex => ex.type !== 'rest').length
                if (block.completed) {
                    qtyExercises += exercises
                    completed += exercises
                } else qtyExercises += exercises
            })

            return parseInt((completed * 100) / qtyExercises) || 0
        },
        exercisesQuantity(vm = this) {
            let exercises = 0
            vm.assignedRoutine?.blocks?.forEach(block => {
                exercises += block?.exercises?.filter(exercise => exercise?.type === 'exercise')?.length || 0
            })
            return exercises
        },
    },
    async mounted() {
        const vm = this
        if (vm.equipment.length === 0) await vm.bindEquipment()
    },
    methods: {
        ...mapActions(['bindEquipment']),
        setAssignedRoutine() {
            const vm = this
            vm.assignedRoutine = vm.routine
            vm.openBlocks = [0]
        },
        blockExercises(block) {
            return block.exercises?.filter(ex => ex.type === 'exercise').length || 0
        },
        circuitType(block) {
            if (!block.isCircuit) return ''
            return `- ${this.$t(`circuits.${block.type}`)}`
        },
        blockMeta(block) {
            const vm = this
            return `${vm.blockExercises(block)} ${vm.$t('common.exercises_short')} · ${vm.blockDuration(block)}`
        },
        blockDuration(block) {
            const vm = this
            if (block.isCircuit) {
                let sets = block.sets
                if (block.type === 'time') return `${sets} ${vm.$t('common.laps').toLowerCase()}`
                if (block.type === 'tabata') {
                    let { minutes, timeWork, timeRest } = block.tabata || {}
                    return `(${timeWork}"x${timeRest}"x${minutes}')x${sets}'`
                }
                return `${sets}'`
            }
            let minutes = 0
            block.exercises.forEach(exercise => {
                minutes += exercise?.config?.sets?.length || block.sets || 0
            })
            let minutesAdded = Number(minutes * 1.3).toFixed()
            if (Number(minutes) === Number(minutesAdded)) return `${minutes}m`
            return `${minutes}-${minutesAdded}m`
        },
        toggleBlock(i) {
            const vm = this
            const idx = vm.openBlocks.indexOf(i)
            if (idx >= 0) vm.openBlocks.splice(idx, 1)
            else vm.openBlocks.push(i)
        },
        onToggleBlock(block, i) {
            const vm = this
            if (vm.loadingComplete) return
            if (block.completed) return vm.setBlockComplete(false, i)
            if (block.isCircuit) {
                vm.circuitBlockIndex = i
                vm.$nextTick(() => vm.$refs.circuitTimer && vm.$refs.circuitTimer.show())
                return
            }
            vm.setBlockComplete(true, i)
        },
        onCircuitTimerHide() {
            const vm = this
            if (vm.circuitBlockIndex === null) return
            vm.setBlockComplete(true, vm.circuitBlockIndex)
        },
        async setBlockComplete(completed, block) {
            const vm = this
            vm.completingIndex = block

            await vm.withLoading(async () => {
                let routineRef = await db.collection('assignedRoutines').doc(vm.assignedRoutine.id).get()
                let assignedRoutine = routineRef.data()
                if (!assignedRoutine) return vm.notifyAssignedRoutineGone(vm.assignedRoutine.id)

                assignedRoutine.blocks[block].completed = completed
                assignedRoutine.blocks[block].exercises.map(exercise => {
                    if (exercise.type === 'exercise') exercise.completed = completed
                })

                if (!await vm.saveAssignedRoutine(vm.assignedRoutine.id, { blocks: assignedRoutine.blocks })) return

                vm.assignedRoutine.blocks[block].completed = completed
            }, 'loadingComplete')

            vm.completingIndex = null
            vm.circuitBlockIndex = null
        },
        onClickExerciseImage(exercise) {
            const vm = this;
            const isYoutubeVideo = exercise?.youtubeUrl ? true : false
            const videoUrl = isYoutubeVideo ? exercise.youtubeUrl : exercise?.video?.url || null
            if (!videoUrl) return;
            let img = exercise.img || (exercise?.video?.thumb) || null
            if (isYoutubeVideo) img = vm.getYoutubeImgUrl(exercise.youtubeUrl)
            vm.img = img || require(`@/assets/img/brand_mark.png`)
            vm.video = videoUrl
            vm.$nextTick(() => {
                if (vm.$refs.watchVideo) vm.$refs.watchVideo.show(isYoutubeVideo)
            })
        },
        onClickExerciseConfig(exercise, block, blockIndex, exerciseIndex) {
            const vm = this;
            vm.$refs.editSets.show({
                assignedRoutineId: vm.assignedRoutine.id,
                exerciseId: exercise.id,
                config: exercise.config,
                block: blockIndex + 1,
                name: exercise.name,
                blockData: block,
                routine: vm.assignedRoutine,
                category: exercise.categories?.[0] || null,
                exerciseIndex: exerciseIndex,
            })
        },
        exerciseAt(blockIndex, exerciseIndex) {
            return this.assignedRoutine?.blocks?.[blockIndex]?.exercises?.[exerciseIndex] || null
        },
        onUpdatedSet({ block, exerciseIndex, config }) {
            const exercise = this.exerciseAt(block - 1, exerciseIndex)
            if (exercise) exercise.config = config
        },
        onSentCommentary({ comment, blockIndex, exerciseIndex }) {
            const exercise = this.exerciseAt(blockIndex, exerciseIndex)
            if (exercise) exercise.commentaryFromStudent = comment
        },
        // MENU ACTIONS
        handleTimer() {
            const vm = this;
            vm.$refs.timer.show()
        },
        handleExercise(exercise, blockIndex, exerciseIndex) {
            const vm = this
            vm.exerciseEditing = {
                ...exercise,
                blockIndex: blockIndex + 1,
                exerciseIndex: exerciseIndex,
            }
            vm.$refs.changeExercise.show()
        },
        async onChangeExerciseSuccess({ oldExercise, newExercise }) {
            const vm = this;

            let currentRoutine = { ...vm.assignedRoutine }
            const blockIndex = vm.exerciseEditing.blockIndex - 1
            const exerciseIndex = vm.exerciseEditing.exerciseIndex
            currentRoutine.blocks[blockIndex].exercises.splice(exerciseIndex, 1, newExercise)

            const saved = await vm.saveAssignedRoutine(vm.assignedRoutine.id, {
                'advance.changes': FieldValue.arrayUnion({
                    type: 'exercise',
                    block: vm.exerciseEditing.blockIndex,
                    exerciseId: oldExercise.objectID || oldExercise.id || null,
                    oldExercise: oldExercise.name,
                    newExercise: newExercise.name,
                })
            })
            if (!saved) return
            vm.toast(vm.$t('common.exercise_changed'))
            vm.exerciseEditing = null
        },
        handleCamera(exercise, blockIndex, exerciseIndex) {
            const vm = this
            vm.exerciseEditing = {
                ...exercise,
                block: blockIndex + 1,
                blockIndex: blockIndex + 1,
                exerciseIndex: exerciseIndex,
            }
            vm.$refs.recordVideo.show()
        },
        async handleSkip(exercise, blockIndex) {
            const vm = this;
            const saved = await vm.saveAssignedRoutine(vm.assignedRoutine.id, {
                'advance.changes': FieldValue.arrayUnion({
                    type: 'skip',
                    block: blockIndex + 1,
                    exercise: exercise.name,
                    exerciseId: exercise.objectID || exercise.id || null,
                })
            })
            if (!saved) return
            vm.toast(vm.$t('common.exercise_skipped'))
        },
        handleHistory(exercise) {
            const vm = this;
            vm.$refs.history.show(exercise)
        },
        handlePainScale(exercise, blockIndex) {
            const vm = this;
            vm.exerciseEditing = {
                ...exercise,
                blockIndex: blockIndex + 1,
            }
            vm.$refs.painScale.show()
        },
        async onPainScale(scale) {
            const vm = this;
            const saved = await vm.saveAssignedRoutine(vm.assignedRoutine.id, {
                'advance.changes': FieldValue.arrayUnion({
                    type: 'painScale',
                    block: vm.exerciseEditing.blockIndex,
                    exercise: vm.exerciseEditing.name,
                    exerciseId: vm.exerciseEditing.objectID || vm.exerciseEditing.id || null,
                    scale: scale
                })
            })
            if (!saved) return
            vm.toast(vm.$t('common.pain_scale_saved'))
            vm.exerciseEditing = null
        },
    },
    watch: {
        routine: {
            handler(val) {
                if (val) this.setAssignedRoutine()
            },
            immediate: true,
        },
    }
}
</script>

<style scoped>
.zap-column {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    box-shadow: 0 1px 0 0 var(--v-outline2-base) inset;
    clip-path: inset(0 round 12px);
}

.zap-col-header {
    padding: 12px 16px 0;
    flex-shrink: 0;
    border-bottom: 1px solid var(--v-outline-base);
    background: linear-gradient(180deg, var(--v-surface2-base), var(--v-surface-base));
    position: sticky;
    top: var(--zap-head-top, 52px);
    z-index: 3;
}

.zap-col-athlete {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
}

.zap-col-avatar {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.zap-col-name {
    flex: 1;
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.zap-mini-pct {
    font-size: 10px;
    color: var(--v-txtMuted-base);
    font-weight: 600;
    flex-shrink: 0;
}

.zap-col-stats {
    display: flex;
    gap: 6px;
    padding: 10px 16px;
}

.zap-col-stat-pill {
    flex: 1;
    background: var(--v-bg-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 6px;
    padding: 4px 6px;
    text-align: center;
}

.zap-col-stat-pill .label {
    font-size: 8px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.zap-col-stat-pill .val {
    font-size: 13px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
    display: block;
}

.zap-col-progress-track {
    height: 4px;
    margin: 0 -16px;
    background: var(--v-surface2-base);
    flex-shrink: 0;
}

.zap-col-progress-fill {
    height: 100%;
    background: var(--brand-gradient);
    transition: width 0.4s ease;
}

.zap-col-blocks {
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.zap-col-block {
    flex-shrink: 0;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    overflow: hidden;
    transition: border-color 0.15s;
}

.zap-col-block.done {
    border-color: rgba(0, 153, 102, 0.3);
}

.zap-col-block.done .zap-col-block-header {
    background: rgba(0, 153, 102, 0.07);
}

.zap-col-block-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    cursor: pointer;
    user-select: none;
    transition: background 0.12s;
}

.zap-col-block-header:hover {
    background: rgba(51, 153, 153, 0.06);
}

.zap-col-toggle {
    width: 28px;
    height: 16px;
    border-radius: 8px;
    background: var(--v-outline2-base);
    position: relative;
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;
}

.zap-col-toggle::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--v-txtMuted-base);
    transition: all 0.2s;
}

.zap-col-toggle.loading {
    opacity: 0.6;
}

.zap-col-block.done .zap-col-toggle {
    background: var(--v-success-base);
}

.zap-col-block.done .zap-col-toggle::after {
    transform: translateX(12px);
    background: #fff;
}

.zap-col-block-title {
    flex: 1;
    min-width: 0;
    font-size: 11px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    letter-spacing: 0.03em;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.zap-col-block-meta {
    flex-shrink: 0;
    font-size: 10px;
    color: var(--v-accent-base);
    font-weight: 500;
    white-space: nowrap;
}

.zap-col-block-chevron {
    flex-shrink: 0;
    transition: transform 0.18s;
}

.zap-col-block-chevron .v-icon {
    color: var(--v-txtMuted-base) !important;
}

.zap-col-block.open .zap-col-block-chevron {
    transform: rotate(180deg);
}

.zap-col-exercises {
    border-top: 1px solid var(--v-outline-base);
}

.zap-col-rest {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    font-size: 10px;
    color: var(--v-txtMuted-base);
    border-top: 1px solid var(--v-outline-base);
}

.zap-col-rest .v-icon {
    color: var(--v-txtMuted-base) !important;
}

.zap-col-circuit-action {
    display: flex;
    justify-content: flex-end;
    padding: 8px 12px;
    border-top: 1px solid var(--v-outline-base);
}
</style>
